import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
} from "@mui/material";
import { Response } from "../../data/registration";

interface Props {
    open: boolean;
    question: Response | null;
    onClose: () => void;
    onSave: (answers: string[]) => void;
}

const UpdateResponseModal: React.FC<Props> = ({ open, question, onClose, onSave }) => {
    const [answers, setAnswers] = useState<string[]>([]);

    useEffect(() => {
        setAnswers(question?.answers ?? []);
    }, [question]);

    const handleChange = (index: number, value: string) => {
        const updated = [...answers];
        updated[index] = value;
        setAnswers(updated);
    };

    const handleAddAnswer = () => setAnswers([...answers, ""]);
    const handleRemoveAnswer = (index: number) => {
        const updated = [...answers];
        updated.splice(index, 1);
        setAnswers(updated);
    };

    const handleSave = () => onSave(answers);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                style: {
                    borderRadius: "12px",
                    padding: "8px",
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 700, fontSize: "22px", textAlign: "center" }}>
                Edit Answers
            </DialogTitle>

            <DialogContent sx={{ padding: "20px 24px" }}>
                <Stack spacing={2} mt={1}>
                    {answers.map((answer, index) => (
                        <Stack key={index} direction="row" spacing={1} alignItems="center">
                            <TextField
                                fullWidth
                                label={`Answer ${index + 1}`}
                                value={answer}
                                onChange={(e) => handleChange(index, e.target.value)}
                                variant="outlined"
                                size="small"
                            />
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                sx={{
                                    height: "40px",
                                    fontWeight: 600,
                                }}
                                onClick={() => handleRemoveAnswer(index)}
                            >
                                Remove
                            </Button>
                        </Stack>
                    ))}
                    <Button
                        variant="outlined"
                        size="small"
                        sx={{
                            alignSelf: "flex-start",
                            fontWeight: 600,
                            borderColor: "#bbb",
                            color: "#555",
                            "&:hover": {
                                borderColor: "#888",
                                backgroundColor: "#f5f5f5",
                            }
                        }}
                        onClick={handleAddAnswer}
                    >
                        Add Another Answer
                    </Button>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "center", paddingBottom: "20px", gap: "16px" }}>
                {/* Cancel Button */}
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                        width: "120px",
                        fontWeight: 600,
                        color: "#6b6b6b",
                        borderColor: "#bbb",
                        "&:hover": {
                            borderColor: "#999",
                            backgroundColor: "#f0f0f0",
                            color: "#333",
                        }
                    }}
                >
                    Cancel
                </Button>

                {/* Save Button */}
                <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                        width: "140px",
                        fontWeight: 600,
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        "&:hover": {
                            backgroundColor: "#1565c0",
                        }
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdateResponseModal;
